/**
 * PricingPage — the live /pricing route (T-242, us2 lane).
 *
 * T-242 removes the dormant UK-styled draft: the misplaced placeholder
 * storefront literal (see the removed draft) is replaced by the modern
 * resolution in services/fastspringStorefront.ts (resolveStorefront /
 * loadUs2Storefront, REACT_APP_FASTSPRING_STORE_ID env).
 * If the store id is not baked, the page fails CLOSED: checkout buttons
 * never mount (busy buttons without error text would look broken).
 *
 * Cards come from GET api/usd-catalog/plans (F-284 live tier pricing,
 * admin-editable per F-173/174) rendered USD-native via formatUsd. The
 * per-unit top-up strip (US-146 remainder) reads the same plans payload
 * (perUnitRate, includedUnits) so the widget's /pricing?plan=X&topup=1
 * intent is answered on the page without a second request.
 *
 * Checkout contract (verified on LP main, dispatch 233b419): usd-catalog.js
 * carries NO checkout endpoint ("No checkout here — F-285 wires the popup +
 * webhook path"), so popupData is built CLIENT-SIDE: the per-unit SKU path
 * is texas-overage-<tierId> (kind 'overage', price = perUnitRate) and the
 * tier subscription path is the plan's own productPath
 * ('landlord-pro-<tierId>') — exactly the payload
 * getEffectiveProductSyncPayload exposes at GET api/usd-catalog/products
 * (F-175). NO invented SKUs, NO fabricated server response: the popup opens
 * with a real catalog path at a real catalog price; FastSpring's own store
 * resolves the line item. If the SBL fails to load or the builder is gone,
 * the page errors and re-arms.
 *
 * Landing scopes from the allowance widget (T-146-002):
 *   /pricing?plan=<tier>&topup=1   → per-unit top-up strip
 *   /pricing?plan=<tier>&upgrade=1 → recommendation line above the cards
 * Unknown ?plan= values are ignored (the user just sees the catalog).
 *
 * Dismissed popup (closed without a completed order, no user effort) shows
 * a re-open CTA.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { landlordApi } from '../services/api';
import { FIRST_FRUITS_TRIAL_DAYS } from '../services/usdPricingModel';
import { startFreeTrial, TRIALABLE_TIERS } from '../services/trialFlow';
import {
  attachPopupSession,
  loadUs2Storefront,
  openPopupSession,
  resolveStorefront,
} from '../services/fastspringStorefront';
import './PricingPage.css';

interface UsdPlan {
  id: string;
  name?: string;
  price: number;
  currency: string;
  active?: boolean;
  productPath?: string;
  perUnitRate?: number;
  includedUnits?: number;
  // Additive F-173 fields the page simply displays when present.
  baseFee?: number;
  unlockedServices?: string[];
  [key: string]: unknown;
}

/** F-200/US-153: one per-use service from GET api/usd-catalog/services. */
interface UsdService {
  type: string;
  name?: string;
  description?: string;
  price: number;
  currency?: string;
  planAccess?: string;
}

const formatUsd = (cents: number): string =>
  `$${(Math.max(cents, 0) / 100).toFixed(2)}`;

/** Service prices are integer USD dollars in the catalog (config source). */
const formatServiceUsd = (dollars: number): string =>
  `$${(Number.isFinite(dollars) && dollars >= 0 ? dollars : 0).toFixed(2)}`;

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [plans, setPlans] = useState<UsdPlan[]>([]);
  const [servicesCatalog, setServicesCatalog] = useState<UsdService[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [focusedTier, setFocusedTier] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  // F-201 / US-152: 14-day no-card trial start from the tier card.
  const [trialBusy, setTrialBusy] = useState<string | null>(null);
  const [trialBanner, setTrialBanner] = useState<{ kind: 'ok' | 'info' | 'err'; text: string } | null>(null);

  const pendingRef = useRef<null | {
    type: 'topup' | 'tier';
    tierId: string;
  }>(null);

  const storeReady = resolveStorefront().ok;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await landlordApi.get('api/usd-catalog/plans');
        if (cancelled) return;
        const data = res?.data?.data ?? res?.data ?? {};
        const loaded: UsdPlan[] = Array.isArray(data.plans)
          ? data.plans.filter((p: UsdPlan) => p && p.id)
          : [];
        setPlans(loaded);
        setError(null);
      } catch (err: any) {
        if (cancelled) return;
        setError(
          err?.response?.status
            ? 'Could not load pricing plans.'
            : 'Could not load pricing plans — connection issue.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // F-200/US-153: the per-use service catalog from the LIVE backend
  // (GET api/usd-catalog/services — { currency, services }). Fail-soft and
  // separate from the plans fetch: a services hiccup must never blank the
  // tier cards, and no pricing data is ever invented client-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await landlordApi.get('api/usd-catalog/services');
        if (cancelled) return;
        const data = res?.data?.data ?? res?.data ?? {};
        const services: UsdService[] = Array.isArray(data.services)
          ? data.services.filter((s: UsdService) => s && s.type && Number.isFinite(Number(s.price)))
          : [];
        setServicesCatalog(services);
      } catch {
        if (!cancelled) setServicesCatalog(null); // section simply hides
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const subscribingRef = useRef(false);
  useEffect(() => {
    subscribingRef.current = subscribing !== null;
  }, [subscribing]);

  // Returning focus after a cancelled popup re-arms the buttons (no callback
  // fires on explicit SBL Cancel; the focus event is the only signal).
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible' && subscribingRef.current) {
        setSubscribing(null);
      }
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, []);
  useEffect(() => {
    const plan = searchParams.get('plan');
    setFocusedTier(plan && plans.some((p) => p.id === plan) ? plan : null);
  }, [searchParams, plans]);

  // Popup closed without a completed order (no user effort) → dismissed.
  // An explicit user Cancel arrives as close(cancelled=true): SBL invokes
  // no callback; the focus returning when the popup closes re-arms the buttons
  // (a disabled state never survives a refocus).
  useEffect(() => {
    attachPopupSession({
      onClosed: () => {
        pendingRef.current = null;
        setDismissed(true);
        setSubscribing(null);
      },
      onData: () => {
        /* order lifecycle detail — webhook (F-195) is authoritative. */
      },
      onError: (code: string, message: string) => {
        setError(message || `Payment error (${code})`);
        setSubscribing(null);
      },
    });
    return () => {
      (window as any).onUs2FsPopupClosed = undefined;
      (window as any).onUs2FsData = undefined;
      (window as any).onUs2FsError = undefined;
    };
  }, []);

  /**
   * Build the SBL payload locally from the F-284 catalog (see module
   * comment). popupData keeps the { products, tags, contact } shape other
   * FastSpring integrations use, just built client-side.
   */
  const startCheckout = useCallback(
    async (entry: { type: 'topup' | 'tier'; tierId: string }) => {
      const token = localStorage.getItem('idToken');
      if (!token) {
        navigate('/login?redirect=/pricing');
        return;
      }

      setError(null);
      setSubscribing(`${entry.type}-${entry.tierId}`);
      pendingRef.current = entry;

      try {
        // Load the SBL for the us2 storefront before opening the popup.
        await loadUs2Storefront();

        let path: string | undefined;
        try {
          // Authoritative live source: the FastSpring sync payload
          // (kind 'overage' | 'subscription') — no drift if the LP config
          // later renames a bundle path. Note the entry.type→kind mapping:
          // 'topup' → 'overage', 'tier' → 'subscription'.
          const kind = entry.type === 'topup' ? 'overage' : 'subscription';
          const res = await landlordApi.get('api/usd-catalog/products');
          const data = res?.data?.data ?? res?.data ?? {};
          const products: any[] = Array.isArray(data.products) ? data.products : [];
          const match = products.find(
            (p) => p && p.tierId === entry.tierId && p.kind === kind
          );
          path = match?.productPath;
        } catch (err: any) {
          // products fetch failed (stage drift / endpoint unreachable):
          // fall through to the F-175 path builders below — same SKUs, no
          // invented ones.
        }
        if (!path) {
          if (entry.type === 'topup') {
            path = `texas-overage-${entry.tierId}`;
          } else {
            const plan = plans.find((p) => p.id === entry.tierId);
            if (!plan?.productPath) {
              throw new Error('Checkout SKU not configured for this plan yet.');
            }
            path = plan.productPath;
          }
        }

        openPopupSession({
          products: [{ path, quantity: 1 }],
          tags: { tier: entry.tierId }, // ties the SBL order session to the tier
          contact: { email: '' }, // FastSpring collects contact in the popup.
        });
        setDismissed(false);
      } catch (err: any) {
        setError(err?.message || 'Failed to start checkout.');
        setSubscribing(null);
        pendingRef.current = null;
      }
    },
    [navigate, plans]
  );

  /**
   * F-201 US-152: the trial path — no FastSpring, no card. Server decides
   * auth/conflict (401 → signup funnel with the chosen plan; 409 → inline
   * message); a start persists the trial markers localStorage so the navbar
   * (subscriptionStatus === 'trial') and TrialConversionModal react.
   */
  const handleTrial = useCallback(
    async (tierId: string) => {
      setTrialBusy(tierId);
      setTrialBanner(null);
      try {
        const outcome = await startFreeTrial(tierId);
        if (outcome.status === 'started') {
          setTrialBanner({
            kind: 'ok',
            text: `Your ${FIRST_FRUITS_TRIAL_DAYS}-day free trial is live — no credit card required.`,
          });
        } else if (outcome.status === 'needs-signup') {
          navigate(`/signup?plan=${tierId}`);
        } else if (outcome.status === 'conflict') {
          setTrialBanner({
            kind: 'info',
            text: outcome.message || 'You already have an active trial or subscription.',
          });
        } else {
          setTrialBanner({
            kind: 'err',
            text: outcome.message || 'Could not start your trial — please try again.',
          });
        }
      } finally {
        setTrialBusy(null);
      }
    },
    [navigate]
  );

  if (loading) {
    return <div className="pricing-loading">Loading pricing plans...</div>;
  }

  const focused = plans.find((p) => p.id === focusedTier) || null;

  return (
    <>
      {!storeReady && (
        <div className="pricing-notice" data-testid="pricing-store-missing" role="alert">
          Online checkout is not configured for this build — contact us or try again later.
        </div>
      )}
      <div className="pricing-page">
      <header className="pricing-header">
        <h1>Choose Your Compliance Solution</h1>
        <p>One flat price per tier — no per-unit creep. The {FIRST_FRUITS_TRIAL_DAYS}-day full-access trial is free.</p>
        <p className="pricing-compare-link">
          <Link to="/compare" data-testid="pricing-compare-link">Compare plans side by side →</Link>
        </p>
      </header>

      {trialBanner && (
        <div
          className={trialBanner.kind === 'err' ? 'pricing-error' : 'pricing-notice'}
          role="status"
          data-testid="pricing-trial-banner"
        >
          {trialBanner.text}
        </div>
      )}

      {error && (
        <div className="pricing-error" role="alert" data-testid="pricing-error">
          {error}
        </div>
      )}

      {/* US-146 remainder: /pricing?plan=<tier> lands here (top-up strip + focus) */}
      {focused && (
        <div className="pricing-notice pricing-notice-focused" data-testid="pricing-focused">
          Recommended for you: <strong>{focused.name || focused.id}</strong>. You do not need to
          change tiers to get extra service calls — top up per unit below.
        </div>
      )}

      {/* US-146 remainder: per-unit top-up strip (overage SKU, F-175) */}
      {focused && focused.perUnitRate != null && (
        <section className="pricing-topup" data-testid="pricing-topup" aria-label="Per-unit top-up">
          <h2>Top up {focused.name || focused.id} service calls</h2>
          <p>
            {focused.includedUnits ?? '—'} calls included each cycle; extra calls cost{' '}
            <strong>{formatUsd(focused.perUnitRate)}</strong> per unit.
          </p>
          <button
            className="subscribe-btn topup"
            onClick={() => startCheckout({ type: 'topup', tierId: focused.id })}
            disabled={!storeReady || subscribing !== null}
            data-testid="pricing-topup-buy"
          >
            {subscribing === `topup-${focused.id}`
              ? 'Preparing checkout…'
              : storeReady
                ? `Buy units — ${formatUsd(focused.perUnitRate)} each`
                : 'Checkout unavailable in this build'}
          </button>
        </section>
      )}

      <div className="products-container">
        {plans.map((plan) => {
          if (!plan.active) return null;
          return (
            <div
              key={plan.id}
              className={`product-section ${plan.id === focusedTier ? 'featured' : ''}`}
              data-testid={`pricing-tier-${plan.id}`}
            >
              <div className="product-header">
                <h2>{plan.name || plan.id}</h2>
                <p>{(plan as any).tagline || 'Compliance tracking for US landlords.'}</p>
              </div>

              <div className="price">
                <span className="amount">{formatUsd(plan.price ?? 0)}</span>
                <span className="period">/month</span>
              </div>

              {Array.isArray(plan.unlockedServices) && plan.unlockedServices.length > 0 && (
                <ul className="features-list">
                  {plan.unlockedServices.map((s) => (
                    <li key={s}>
                      <span className="check">✓</span>
                      {s}
                    </li>
                  ))}
                </ul>
              )}

              {(TRIALABLE_TIERS as readonly string[]).includes(plan.id) && (
                <button
                  className="subscribe-btn trial"
                  onClick={() => handleTrial(plan.id)}
                  disabled={trialBusy !== null}
                  data-testid={`pricing-trial-${plan.id}`}
                >
                  {trialBusy === plan.id
                    ? 'Starting your trial…'
                    : `Start my ${FIRST_FRUITS_TRIAL_DAYS}-day free trial`}
                </button>
              )}

              <button
                className="subscribe-btn"
                onClick={() => startCheckout({ type: 'tier', tierId: plan.id })}
                disabled={!storeReady || subscribing !== null}
                data-testid={`pricing-buy-${plan.id}`}
              >
                {subscribing === `tier-${plan.id}`
                  ? 'Preparing checkout…'
                  : storeReady
                    ? 'Subscribe Now'
                    : 'Checkout unavailable in this build'}
              </button>
            </div>
          );
        })}
      </div>

      {/* F-200/US-153: per-use service catalog — LIVE data only, never
          invented. Every figure comes straight from the services payload. */}
      {servicesCatalog && servicesCatalog.length > 0 && (
        <section className="services-catalog" data-testid="services-catalog" aria-label="Per-use services">
          <h2>Per-use services</h2>
          <p>Pay only when you need one — no subscription required beyond the tier that unlocks it.</p>
          <ul className="services-list">
            {servicesCatalog.map((s) => {
              const accessTier = plans.find((p) => p.id === s.planAccess);
              const accessLabel = accessTier?.name || (s.planAccess ? s.planAccess.charAt(0).toUpperCase() + s.planAccess.slice(1) : null);
              return (
                <li key={s.type} className="service-row" data-testid={`service-${s.type}`}>
                  <div className="service-info">
                    <strong>{s.name || s.type}</strong>
                    {s.description && <span className="service-desc">{s.description}</span>}
                  </div>
                  <div className="service-meta">
                    <span className="service-price">{formatServiceUsd(s.price)} per use</span>
                    {accessLabel && (
                      <span className="service-access">Included from {accessLabel}</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Popup closed without a completed order → re-open CTA. */}
      {dismissed && (
        <div className="pricing-error pricing-error-dismissed" role="status" data-testid="popup-dismissed">
          Checkout window closed before completion — no payment was taken.
          {pendingRef.current && (
            <button onClick={() => startCheckout(pendingRef.current!)}>Re-open checkout</button>
          )}
        </div>
      )}
      </div>
    </>
  );
};

export default PricingPage;
